/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import { ofType } from 'redux-observable';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import {
  dispatchCorrectActionIfChildOrRemote,
  getActionPayload,
  mergeCurrentModalContentToTarget,
  processDataToDispatchErrorAction
} from '../../store/state/epics/util';
import { getTranslatedDXAPIResponse } from '../../utils/dxapi-response-translator';
import { getDataObject } from '../../store/state/state-manager';
import { ERROR, ACTION_CREATEOBJECT, EXECUTE_OBJECT_WIDE_ACTION } from '../../store/state/actions';
import Constants, { publicConstants } from '../../constants';
import Utils from '../../utils/utils';
import { ActionManagerInstance } from '../../actions/manager';
import ActionsSequencer from '../../actions/sequencer';
import {
  getObjectClassName,
  getObjectInfoID,
  updateKeyAndFlowName,
  setLastRefreshTimeProperty,
  deferCaseSummaryViewUpdate,
  isNextStepInCreateStage
} from '../common-helper';
import { getDisplayCaseID } from '../../case/common-helper';
import { publishEndOfAssignmentProcessingEvent } from '../../case/common-events';
import { getSemanticURL } from '../../router/app-router';
import {
  getTargetFromContainerItemID,
  getActiveContainerItemName,
  getChildContainers
} from '../../container/container-utils';
import { getParentContextName } from '../../utils/context-utils';
import { isFormValid } from '../../utils/form-handler';
import { clearMessages } from '../../messages';
import DataApi from '../../data-view';
import type { Action } from '../../store/state/state-manager';
import type { ToastMessage, Headers, ChainedDataPayload, ActionsOut, DxAPIResponseObject } from '../../case/types';
import {
  mutateErroneousFields,
  addRemoveTransientItemAction,
  createToastMessage,
  addActionToActivateModalContainerItem,
  isModalLaunchedFromPrimary,
  addActionsForFinishAssignmentLaunchedFromModal,
  publishEventsForLocalActionFromPreviewView
} from '../../case/finish-assignment/helper';
import { publishExpressLocalActionSubmitEvent } from '../../case/finish-assignment/events';
import ObjectTransformer from '../../utils/object-transformer';
import { publishCreateStageDoneEvent } from '../../case/create-work/events';

import { executeObjectWideActionRequest } from './async-request';
import getPayloadForEndOfAssignmentProcessingEvent from './helper';

const { RESOURCE_TYPES } = publicConstants;

const executeObjectWideAction = (action$: Observable<Action>, state$: { value: any }) => {
  let actionMgrID = 0;
  let actionsOut: ActionsOut[] = [];
  let toastMessage: ToastMessage = {};
  let actionContext = '';

  return action$.pipe(
    ofType(EXECUTE_OBJECT_WIDE_ACTION),
    switchMap((action: Action) => {
      ({ actionMgrID } = action.payload);
      actionContext = action.payload.context;
      clearMessages({
        context: actionContext,
        stateObj: getDataObject(actionContext),
        clearContextMessages: true
      });
      if (!isFormValid(actionContext)) {
        const error = { type: publicConstants.SUBMISSION_FAILURE_TYPE.FORM_VALIDATION_ERROR };
        ActionManagerInstance.rejectActionByID(actionMgrID, true, error);
        ActionsSequencer.handleDeferredActionCompletion(action.payload.context);
        // return empty observable as form is not valid.
        return of([]);
      }
      return of(action.payload).pipe(
        mergeMap((payload) => {
          // To identify the current opened modal in ModalViewContainer, we are activating current container on every finishAssignment
          addActionToActivateModalContainerItem(actionsOut, payload);
          const mutatingPayload = { ...payload };
          return executeObjectWideActionRequest(mutatingPayload, { state$ });
        }),
        mergeMap(
          async (chainedData: {
            type: string;
            context: string;
            headers: Headers;
            actionInPayload: Action['payload'];
            response: DxAPIResponseObject;
            payload: ChainedDataPayload;
          }) => {
            if (chainedData?.type === ERROR) {
              const errorAction = processDataToDispatchErrorAction(chainedData);
              actionsOut.push(errorAction);
              return actionsOut;
            }
            // clear DataApi caches after Submit is success
            DataApi.clearContextedCache(actionContext);
            const { headers, actionInPayload, response } = chainedData;
            const { containerItemID, isModalAction, isInCreateStage } = actionInPayload;
            const target = getTargetFromContainerItemID(containerItemID);
            actionInPayload.target = target;

            Utils.setIn(response.data, Constants.OBJECT_INFO_IS_LOCAL_ACTION, false);

            publishExpressLocalActionSubmitEvent({ submitResponse: response });

            let translatedDXAPIResponse = getTranslatedDXAPIResponse(response, {
              [publicConstants.STOPMERGEACTIONS]: !response.nextAssignmentInfo
            });
            /* Use stopMergeActions to restrict to display localActions in Actions Menu in Review Mode. */
            const caseClassName = getObjectClassName(translatedDXAPIResponse);
            const parentContainerItemID = getParentContextName(actionInPayload.context);
            const activeWorkArea = getChildContainers(parentContainerItemID)[0]
              ? getActiveContainerItemName(getChildContainers(parentContainerItemID)[0])
              : parentContainerItemID;
            const isNextStepInCreateStageVariable = isNextStepInCreateStage(translatedDXAPIResponse);
            const isLastAssignmentInCreateStage = isInCreateStage && !isNextStepInCreateStageVariable;

            if (
              isModalAction &&
              !isInCreateStage &&
              !isModalLaunchedFromPrimary(actionInPayload.context, activeWorkArea)
            ) {
              translatedDXAPIResponse = mergeCurrentModalContentToTarget(
                translatedDXAPIResponse,
                getDataObject(containerItemID)
              );
            }

            /* commonOutActionPayload will get mutated down the process, based on use cases. */
            const commonOutActionPayload = getActionPayload(translatedDXAPIResponse, {
              headers,
              actionMgrID,
              directUrlUpdation: false
            });
            /* lastRefreshTime property will be consumed by files & attachments widget or any other to get refreshed per every finish assignment submit */
            setLastRefreshTimeProperty(commonOutActionPayload.data, actionInPayload);
            commonOutActionPayload.caseClassName = caseClassName;
            commonOutActionPayload.resourceType = RESOURCE_TYPES.ASSIGNMENT;
            commonOutActionPayload.headers = headers;
            const isNextAssignmentInfoPresent = false;
            const caseInfoID = getObjectInfoID(translatedDXAPIResponse);
            updateKeyAndFlowName(commonOutActionPayload, translatedDXAPIResponse);
            const semanticURL = getSemanticURL(ACTION_CREATEOBJECT, { caseClassName }).replace(
              /{.+?}/,
              String(getDisplayCaseID(caseInfoID))
            );
            translatedDXAPIResponse.semanticURL = semanticURL;
            commonOutActionPayload.semanticURL = semanticURL;

            // Removed isModalAction flag if block, as this is always true in case of object actions.
            await addActionsForFinishAssignmentLaunchedFromModal(
              actionInPayload,
              commonOutActionPayload,
              isNextAssignmentInfoPresent,
              caseInfoID,
              translatedDXAPIResponse,
              actionsOut,
              chainedData,
              response,
              true
            );
            // As this is independent flag, removed from if nested conditions.
            if (isLastAssignmentInCreateStage) {
              toastMessage = createToastMessage(actionInPayload);
              publishCreateStageDoneEvent({
                caseId: actionInPayload.caseID,
                caseType: actionInPayload.caseTypeID,
                assignmentId: actionInPayload.assignmentID
              });
            }
            deferCaseSummaryViewUpdate(chainedData, actionsOut);
            ActionsSequencer.handleDeferredActionCompletion(actionContext);
            // Use Case: 2 ==> Local action finish assignment for below use cases- a. local action from preview view
            publishEventsForLocalActionFromPreviewView(commonOutActionPayload, actionInPayload, activeWorkArea);

            // publish end of assignment processing event
            const eventPayload = getPayloadForEndOfAssignmentProcessingEvent(translatedDXAPIResponse, actionInPayload);
            publishEndOfAssignmentProcessingEvent({ ...eventPayload, containerItemID });

            addRemoveTransientItemAction(actionsOut, {
              transientItemID: containerItemID,
              target,
              isModalAction
            });

            return actionsOut;
          }
        ),
        map(() => {
          const errorObject = actionsOut.find((obj) => obj.type === ERROR);
          if (errorObject) {
            ActionsSequencer.handleDeferredActionCompletion(actionContext);
            mutateErroneousFields(errorObject);
            ActionManagerInstance.rejectActionByID(actionMgrID);
          } else {
            ActionManagerInstance.resolveActionByID(actionMgrID, toastMessage);
          }
          dispatchCorrectActionIfChildOrRemote(actionsOut);
          ObjectTransformer.setNeedsMapping(false);
          actionsOut = [];
          toastMessage = {};
          return actionsOut;
        })
      );
    })
  );
};

export default executeObjectWideAction;
